import { useEffect, useRef, useState } from "react";

import { IMAGE_VARIANTS } from "~/lib/images/variants";
import type { ImageVariant } from "~/lib/images/variants";

import { AssetPlaceholder } from "./AssetPlaceholder";

export type ImageProps = {
  /** Pre-computed CDN URL from getPublicUrl() or getSignedUrl() — computed by the loader, not here. */
  src: string;
  /** Drives width/height derivation (anti-CLS per T52) and type safety at call sites. */
  variant: ImageVariant;
  /** Required per T50 a11y contract. */
  alt: string;
  loading?: "lazy" | "eager";
  fetchPriority?: "high" | "low" | "auto";
  srcSet?: string;
  sizes?: string;
  className?: string;
};

export function Image({ src, variant, alt, loading = "lazy", fetchPriority, srcSet, sizes, className }: ImageProps) {
  const { width, height } = IMAGE_VARIANTS[variant];
  // T256 safety net: a delivery URL that 404s (e.g. a CF Images variant that was
  // never created in the account) must NEVER show the browser's broken-image icon
  // on the public site. On the client-side error event we swap to the brand
  // placeholder, which reserves the same box (anti-CLS). The SSR pass still emits a
  // real <img> so a healthy variant renders with zero JS; this only fires on a
  // genuine load failure. The complementary `images-setup-variants.mjs --check`
  // catches the missing-variant drift at the ops layer before it ever ships.
  const [failed, setFailed] = useState(false);
  // T762 — an eager image can fail before hydration attaches `onError` (the home hero
  // loads first): on mount, an image the browser has finished with and has no pixels
  // has failed, so it takes the same placeholder.
  const imgRef = useRef<HTMLImageElement>(null);
  useEffect(() => {
    const img = imgRef.current;
    if (img?.complete && img.naturalWidth === 0) setFailed(true);
  }, []);
  if (failed) {
    return <AssetPlaceholder variant={variant} className={className} />;
  }
  return (
    <img
      ref={imgRef}
      src={src}
      alt={alt}
      width={width}
      height={height ?? undefined}
      loading={loading}
      fetchPriority={fetchPriority}
      srcSet={srcSet}
      sizes={srcSet ? sizes : undefined}
      decoding="async"
      className={className}
      onError={() => setFailed(true)}
    />
  );
}
